import React, { useState, useEffect } from "react";
import { Table } from "../../../components/table";
import type { ColumnsType } from "antd/es/table";
import { LoadingData } from "./../components/noDetails";
import { ChevronRight, ChevronDown } from "lucide-react";

interface QuarterData {
  year: string;
  quarter?: number;
  date?: string;
  amount?: number;
}

interface FinancialRowData {
  [key: string]: QuarterData[];
}

interface ApiResponse {
  data: {
    stock_id: number;
    headers: QuarterData[];
    rows: FinancialRowData;
  };
}

interface TableRow {
  key: string;
  lineItem: string;
  isChild?: boolean;
  parentKey?: string;
  [key: string]: string | number | boolean | undefined;
}

interface FormatConfig {
  decimal: string;
  scale: string;
}

interface ReportTableProps {
  activeTab?: string;
  data?: ApiResponse;
  viewMode?: string;
  formatConfig?: FormatConfig;
  currency?: string;
  loading?: boolean;
  hasDecimalBeenModified?: boolean;
}

export const ReportTable: React.FC<ReportTableProps> = ({
  activeTab = "income_statement",
  data,
  viewMode = "quarterly",
  formatConfig = { decimal: "", scale: "" },
  currency = "USD",
  loading,
  hasDecimalBeenModified = false,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const parentChildMap: { [key: string]: string[] } = {
    "Cash flow used in Operating Activities": ["Depreciation", "Amortization"],
    "Cash flow used in Investing Activities": ["Capital Expenditure"],
    "Cash flow from Financing Activities": ["Net Borrowings"],
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const formatHeader = (headerData: QuarterData): string => {
    if (viewMode === "annually") {
      return headerData.year;
    }

    const shortYear = headerData.year.slice(-2);

    if (headerData.date) {
      const date = new Date(headerData.date);
      const monthName = date.toLocaleString("en-US", { month: "short" });
      const shortYear = date.getFullYear().toString().slice(-2);
      return `Q${headerData.quarter} ${monthName}'${shortYear}`;
    }

    return `Q${headerData.quarter} '${shortYear}`;
  };

  const formatAmount = (
    amount: number | undefined,
    isPercentage: boolean = false,
    rowKey: string = "",
  ): string => {
    if (
      amount === undefined ||
      amount === null ||
      isNaN(amount) ||
      amount === 0
    ) {
      return "-";
    }

    const isNegative = amount < 0;
    let scaledValue = Math.abs(amount);
    let suffix = "";

    const excludeFromScaling = [
      "Earnings per Share",
      "Dividends per Share",
      "Book Value",
      "Current Assets Ratio",
      "Quick Ratio",
      "Inventory Days",
      "Receivable Days",
      "Payable Days",
      "Cash Conversion Cycle",
      "Debt To Equity",
      "Free Cash Flow Per Share",
    ];

    if (
      formatConfig.scale &&
      !isPercentage &&
      !excludeFromScaling.includes(rowKey)
    ) {
      switch (formatConfig.scale) {
        case "thousands":
          scaledValue = Math.abs(amount) / 1000;
          suffix = "K";
          break;
        case "millions":
          scaledValue = Math.abs(amount) / 1000000;
          suffix = "M";
          break;
        case "billions":
          scaledValue = Math.abs(amount) / 1000000000;
          suffix = "B";
          break;
        default:
          scaledValue = Math.abs(amount);
          suffix = "";
      }
    }

    let decimalPlaces = 0;

    if (
      rowKey === "Earnings per Share" ||
      rowKey === "Earnings per Share Split Adjusted" ||
      rowKey === "Dividends per Share" ||
      rowKey === "Dividends per Share Split Adjusted" ||
      rowKey === "Debt To Equity" ||
      rowKey === "Free Cash Flow Per Share"
    ) {
      decimalPlaces = 4;
    } else if (
      (isPercentage ||
        rowKey === "Current Assets Ratio" ||
        rowKey === "Quick Ratio" ||
        rowKey === "Free Cash Flow Per Share" ||
        rowKey === "Book Value" ||
        rowKey === "Book Value Split Adjusted") &&
      formatConfig.decimal === "" &&
      !hasDecimalBeenModified
    ) {
      decimalPlaces = 2;
    } else {
      switch (formatConfig.decimal) {
        case "decimal_0":
          decimalPlaces = 1;
          break;
        case "decimal_00":
          decimalPlaces = 2;
          break;
        default:
          decimalPlaces = 0;
      }
    }

    if (
      rowKey === "Inventory Days" ||
      rowKey === "Receivable Days" ||
      rowKey === "Payable Days" ||
      rowKey === "Cash Conversion Cycle"
    ) {
      decimalPlaces = 0;
    }

    const formattedNumber = scaledValue.toLocaleString("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });

    const valueWithSuffix = suffix
      ? `${formattedNumber}${suffix}`
      : formattedNumber;

    return isNegative ? `(${valueWithSuffix})` : valueWithSuffix;
  };

  const hasSpaceAfter = (lineItem: string): boolean => {
    const spaceAfterItems = ["Trade Payables", "Book Value"];
    return spaceAfterItems.includes(lineItem);
  };

  const highlightedItems = [
    "Net Change in Cash",
    "Total Current Assets",
    "Total Assets",
    "Total Equity and Liabilities",
  ];

  const transformDataToTable = (): {
    columns: ColumnsType<TableRow>;
    dataSource: TableRow[];
  } => {
    if (!data?.data?.rows || !data?.data?.headers) {
      return { columns: [], dataSource: [] };
    }

    const headers = data.data.headers;
    const rows = data.data.rows;

    const columns: ColumnsType<TableRow> = [
      {
        title: "",
        dataIndex: "lineItem",
        key: "lineItem",
        fixed: isMobile ? undefined : "left",
        width: 280,
        render: (text: string, record: TableRow) => {
          const isParent = parentChildMap.hasOwnProperty(text);
          const isExpanded = expandedRows.has(record.key);
          const isChild = record.isChild;

          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: isChild ? "24px" : "0px",
                cursor: isParent ? "pointer" : "default",
              }}
              onClick={() => isParent && toggleRow(record.key)}
            >
              {isParent && (
                <span style={{ marginRight: "8px", display: "flex" }}>
                  {isExpanded ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </span>
              )}
              {text}
            </div>
          );
        },
        onCell: (record: TableRow) => {
          const styles: React.CSSProperties = {};

          if (
            record.lineItem.includes("%") ||
            highlightedItems.includes(record.lineItem)
          ) {
            styles.backgroundColor = "#f3f4f6";
          }

          if (hasSpaceAfter(record.lineItem)) {
            styles.paddingTop = "30px";
          }

          // if (record.isChild) {
          //   styles.backgroundColor = "#fafafa";
          // }

          return { style: styles };
        },
      },
    ];

    headers.forEach((header) => {
      const headerKey =
        viewMode === "annually"
          ? `year_${header.year}`
          : `q${header.quarter}_${header.year}`;

      columns.push({
        title: formatHeader(header),
        dataIndex: headerKey,
        key: headerKey,
        align: "right",
        width: 140,
        className: "text-sm",
        render: (value) => value || "-",
        onCell: (record: TableRow) => {
          const styles: React.CSSProperties = {};

          if (
            record.lineItem.includes("%") ||
            highlightedItems.includes(record.lineItem)
          ) {
            styles.backgroundColor = "#f3f4f6";
          }

          if (hasSpaceAfter(record.lineItem)) {
            styles.paddingTop = "30px";
          }

          // if (record.isChild) {
          //   styles.backgroundColor = "#fafafa";
          // }

          return { style: styles };
        },
      });
    });

    const allChildKeys = new Set<string>();
    Object.values(parentChildMap).forEach((children) => {
      children.forEach((child) => allChildKeys.add(child));
    });

    const dataSource: TableRow[] = [];
    let rowIndex = 0;

    Object.entries(rows).forEach(([rowKey, periodData]) => {
      if (allChildKeys.has(rowKey)) {
        return;
      }

      const isParent = parentChildMap.hasOwnProperty(rowKey);
      const parentKey = `row_${rowIndex}`;

      const row: TableRow = {
        key: parentKey,
        lineItem: rowKey,
      };

      const isPercentage = rowKey.includes("%");

      headers.forEach((header) => {
        const headerKey =
          viewMode === "annually"
            ? `year_${header.year}`
            : `q${header.quarter}_${header.year}`;

        const matchingData = periodData.find((item) => {
          if (viewMode === "annually") {
            return item.year === header.year;
          }
          return item.year === header.year && item.quarter === header.quarter;
        });

        const formattedValue = matchingData
          ? formatAmount(matchingData.amount, isPercentage, rowKey)
          : "-";

        row[headerKey] =
          isPercentage && formattedValue !== "-"
            ? `${formattedValue}%`
            : formattedValue;
      });

      dataSource.push(row);
      rowIndex++;

      if (isParent && expandedRows.has(parentKey)) {
        const childKeys = parentChildMap[rowKey];
        childKeys.forEach((childKey) => {
          if (rows[childKey]) {
            const childRow: TableRow = {
              key: `row_${rowIndex}`,
              lineItem: childKey,
              isChild: true,
              parentKey: parentKey,
            };

            const childIsPercentage = childKey.includes("%");

            headers.forEach((header) => {
              const headerKey =
                viewMode === "annually"
                  ? `year_${header.year}`
                  : `q${header.quarter}_${header.year}`;

              const matchingData = rows[childKey].find((item) => {
                if (viewMode === "annually") {
                  return item.year === header.year;
                }
                return (
                  item.year === header.year && item.quarter === header.quarter
                );
              });

              const formattedValue = matchingData
                ? formatAmount(matchingData.amount, childIsPercentage, childKey)
                : "-";

              childRow[headerKey] =
                childIsPercentage && formattedValue !== "-"
                  ? `${formattedValue}%`
                  : formattedValue;
            });

            dataSource.push(childRow);
            rowIndex++;
          }
        });
      }
    });

    return { columns, dataSource };
  };

  const { columns, dataSource } = transformDataToTable();

  return (
    <div className="w-full">
      {loading ? (
        <LoadingData />
      ) : (
        <Table<TableRow>
          columns={columns}
          dataSource={dataSource}
          rowKey="key"
          pagination={false}
          size="small"
          bordered={false}
          scroll={{ x: Math.max(1000, columns.length * 120) }}
          className="financial-table"
          showHeader={true}
          loading={loading}
        />
      )}
    </div>
  );
};
